import { computePosition, offset, flip, shift, arrow } from "@floating-ui/dom";

/**
 * Inicializa todos os tooltips baseados no atributo data-tooltip
 */
export function initFloatingTooltips() {
  const triggers = document.querySelectorAll("[data-tooltip]");

  triggers.forEach((trigger) => {
    initSingleTooltip(trigger);
  });
}

/**
 * Inicializa um único tooltip
 *
 * @param {HTMLElement} triggerElement - Elemento que terá o tooltip
 */
function initSingleTooltip(triggerElement) {
  const tooltipText = triggerElement.getAttribute("data-tooltip");
  if (!tooltipText) return;

  // Obtém o tamanho do tooltip (sm, md, lg) ou usa 'md' como padrão
  const tooltipSize = triggerElement.getAttribute("data-tooltip-size") || "md";

  // Define classes Tailwind baseadas no tamanho
  const sizeClasses = {
    sm: "text-xs px-2 py-1",
    md: "text-sm px-3 py-1.5",
    lg: "text-base px-4 py-2",
  };

  const sizeClass = sizeClasses[tooltipSize] || sizeClasses.md;

  // Obtém a largura máxima do tooltip (pode ser uma classe Tailwind como max-w-xs, max-w-sm, etc)
  const maxWidthClass =
    triggerElement.getAttribute("data-tooltip-max-width") || "";

  // Obtém o alinhamento do texto (text-center, text-left, text-right)
  const textAlignClass =
    triggerElement.getAttribute("data-tooltip-align") || "";

  // Cria o elemento do tooltip
  const tooltip = document.createElement("div");
  tooltip.className =
    `floating-tooltip ${sizeClass} ${maxWidthClass} ${textAlignClass}`.trim();
  tooltip.innerHTML = `
    <span class="floating-tooltip-text">${tooltipText}</span>
    <div class="floating-tooltip-arrow"></div>
  `;

  // Adiciona ao body
  document.body.appendChild(tooltip);

  const arrowElement = tooltip.querySelector(".floating-tooltip-arrow");

  // Aplica estilos inline (para não depender de CSS externo)
  const tooltipStyle = {
    position: "absolute",
    top: "0",
    left: "0",
    backgroundColor: "#ffffff",
    color: "#1f2937",
    borderRadius: "8px",
    fontWeight: "500",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: "9999",
    pointerEvents: "none",
    opacity: "0",
    transform: "scale(0.95)",
    transition: "opacity 0.15s ease, transform 0.15s ease",
  };

  // Se não houver max-width definido, usa whiteSpace: nowrap para não quebrar linha
  if (!maxWidthClass) {
    tooltipStyle.whiteSpace = "nowrap";
  } else {
    // Se houver max-width, permite quebra de linha
    tooltipStyle.wordWrap = "break-word";
  }

  Object.assign(tooltip.style, tooltipStyle);

  Object.assign(arrowElement.style, {
    position: "absolute",
    width: "8px",
    height: "8px",
    backgroundColor: "#ffffff",
    transform: "rotate(45deg)",
  });

  let isVisible = false;

  const updatePosition = () => {
    computePosition(triggerElement, tooltip, {
      placement: "top",
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ["bottom", "left", "right"],
        }),
        shift({ padding: 8 }),
        arrow({ element: arrowElement }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(tooltip.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      // Posiciona a seta
      if (middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placement.split("-")[0]];

        Object.assign(arrowElement.style, {
          left: arrowX != null ? `${arrowX}px` : "",
          top: arrowY != null ? `${arrowY}px` : "",
          right: "",
          bottom: "",
          [staticSide]: "-4px",
        });
      }
    });
  };

  const showTooltip = () => {
    if (isVisible) return;
    isVisible = true;
    updatePosition();
    Object.assign(tooltip.style, {
      opacity: "1",
      transform: "scale(1)",
    });
  };

  const hideTooltip = () => {
    if (!isVisible) return;
    isVisible = false;
    Object.assign(tooltip.style, {
      opacity: "0",
      transform: "scale(0.95)",
    });
  };

  // Event listeners
  triggerElement.addEventListener("mouseenter", showTooltip);
  triggerElement.addEventListener("mouseleave", hideTooltip);
  triggerElement.addEventListener("focus", showTooltip);
  triggerElement.addEventListener("blur", hideTooltip);
}
